<template>
  <div class="alt-markets coupon" v-if="markets && markets.length">
    <div v-for="(altMarketGroup, altMarketGroupName) in groupAltMarkets" :key="altMarketGroupName">
      <!-- Group header -->
      <h4 class="event-market-header date">
        <span
          class="market-name"
          :class="{ 'expanded-market': expandedAltMarketGroups[altMarketGroupName] }"
          @click="toggleAltMarketGroupExpansion(altMarketGroupName)"
        >
          <span class="market-name-title">{{ altMarketGroupName }}</span>
          <span class="flex icon-span">
            <sgp-tag :markets="getSGMProcessedMarkets(altMarketGroup.markets)" :isLive="isLiveEventPage"></sgp-tag>
            <span v-if="isBBenabled && isMarketEligibleForBB(altMarketGroup.markets)" class="available-for-bb"></span>
            <v-icon v-if="!expandedAltMarketGroups[altMarketGroupName]">expand_more</v-icon>
            <v-icon v-else>expand_less</v-icon>
          </span>
        </span>
      </h4>

      <!-- Header with participant names (only if first market has A/H selections) -->
      <div
        v-if="(expandedAltMarketGroups[altMarketGroupName] && altMarketGroup.markets[0].selections.filter(sel => sel.hadvalue === 'A').length > 0)
            || (expandedAltMarketGroups[altMarketGroupName] && altMarketGroup.markets[0].selections.filter(sel => sel.hadvalue === 'H').length > 0)"
        class="alt-handicaps-header"
      >
        <span class="alt-handicaps-header-name">{{ event.participantname_home }}</span>
        <span class="alt-handicaps-header-name">{{ event.participantname_away }}</span>
      </div>

      <!-- Render alt markets inside group -->
      <markets
        v-if="expandedAltMarketGroups[altMarketGroupName]"
        :markets="getVisibleAltMarkets(altMarketGroup.markets, altMarketGroupName)"
        :isAlternativeMarket="true"
        :event="event"
        :diplayOnPage="'live-single-event'"
        :isLiveEventPage="isLiveEventPage"
        :pricechangehighlight="isLiveEventPage"
      >
      </markets>

      <!-- "See More / See Less" button if group has more than 6 selections -->
      <div v-if="altMarketSelectionsCountByGroup[altMarketGroupName] > 6 && expandedAltMarketGroups[altMarketGroupName]">
        <div class="show-more-btn">
          <v-btn :ripple="false" flat @click.stop="toggleAltMarketSelectionsVisibility(altMarketGroupName)">
            <span v-show="!expandedAltMarketSelections[altMarketGroupName]">{{ $t('BettingOffer.SeeMore') }}</span>
            <span v-show="expandedAltMarketSelections[altMarketGroupName]">{{ $t('BettingOffer.SeeLess') }}</span>
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import lodash from 'lodash'

import markets from '@/components/sports/bettingoffer/markets'
import BettingOffer from '@/components/mixins/BettingOffer'
import sgpTag from '@/components/sports/betslipV2/common/marketSgpTag.vue'
import killswitch from '@/components/mixins/killswitch'
import config from '@/config'

export default {
  name: 'AltMarkets',

  components: {
    markets,
    sgpTag
  },

  mixins: [BettingOffer, killswitch],

  props: [
    'markets',
    'event',
    'diplayOnPage',
    'isLiveEventPage',
    'pricechangehighlight',
    'expandFirst'
  ],

  data () {
    return {
      // Expansion state for each alternative market group (true = expanded, false = collapsed)
      expandedAltMarketGroups: {},

      // Selections state per alt market group (true = show all, false = show only first 3)
      expandedAltMarketSelections: {}
    }
  },

  computed: {
    // Groups alternative markets by market.name
    // Markets inside each alt market group are sorted by currentmatchhandicap (descending)
    groupAltMarkets () {
      const groupedAltMarkets = {}

      if (!Array.isArray(this.markets)) return groupedAltMarkets

      this.markets.forEach(market => {
        if (!market || typeof market !== 'object' || !market.name) return

        const altMarketGroupKey = String(market.name).trim()
        if (!altMarketGroupKey) return

        if (!groupedAltMarkets[altMarketGroupKey]) {
          groupedAltMarkets[altMarketGroupKey] = { markets: [] }
        }

        groupedAltMarkets[altMarketGroupKey].markets.push(market)
      })

      // Sort markets inside each alt market group by handicap descending
      Object.values(groupedAltMarkets).forEach(altMarketGroup => {
        altMarketGroup.markets.sort((a, b) => {
          const handicapA = a.currentmatchhandicap != null ? Number(a.currentmatchhandicap) : -Infinity
          const handicapB = b.currentmatchhandicap != null ? Number(b.currentmatchhandicap) : -Infinity
          return handicapB - handicapA
        })
      })

      return groupedAltMarkets
    },
    isBBenabled () {
      return config.app.autoconf.IS_BUYBACK_ENABLED
    },

    // Returns total number of selections per alt market group (sums selections across all markets in the group)
    altMarketSelectionsCountByGroup () {
      const counts = {}
      Object.keys(this.groupAltMarkets).forEach(altMarketGroupName => {
        let totalSelections = 0
        this.groupAltMarkets[altMarketGroupName].markets.forEach(market => {
          if (Array.isArray(market.selections)) {
            totalSelections += market.selections.length
          }
        })
        counts[altMarketGroupName] = totalSelections
      })
      return counts
    }
  },

  methods: {
    // Toggles expansion state of an alternative market group
    toggleAltMarketGroupExpansion (altMarketGroupName) {
      Vue.set(this.expandedAltMarketGroups, altMarketGroupName, !this.expandedAltMarketGroups[altMarketGroupName])
    },

    // Toggles visibility of extra selections inside an alternative market group
    toggleAltMarketSelectionsVisibility (altMarketGroupName) {
      Vue.set(this.expandedAltMarketSelections, altMarketGroupName, !this.expandedAltMarketSelections[altMarketGroupName])
    },

    // Returns either all markets or only the first 3, depending on alt market group state
    getVisibleAltMarkets (altMarkets, altMarketGroupName) {
      if (!Array.isArray(altMarkets)) return altMarkets
      if (this.expandedAltMarketSelections[altMarketGroupName]) {
        return altMarkets
      }
      return altMarkets.slice(0, 3)
    },
    getSGMProcessedMarkets (markets) {
      if (!Array.isArray(markets)) return []
      const processed = this.ksPreProcessMarkets(markets, this.event)
      return processed.filter(market => market.csvpricetypes && market.csvpricetypes.includes('SGM')) || []
    },
    isMarketEligibleForBB (markets) {
      if (!Array.isArray(markets)) return false

      return markets.some(market => market.BB === true)
    }
  },

  watch: {
    // Whenever groupAltMarkets changes:
    // - add new alt market groups with default states
    // - preserve existing states for already known groups
    groupAltMarkets: {
      immediate: true,
      handler (newAltMarketGroups) {
        const newExpandedAltMarketGroups = { ...this.expandedAltMarketGroups }
        const newExpandedAltMarketSelections = { ...this.expandedAltMarketSelections }

        Object.keys(newAltMarketGroups).forEach((altMarketGroupName, index) => {
          if (!(altMarketGroupName in newExpandedAltMarketGroups)) {
            // Open first alt market group if expandFirst is true
            newExpandedAltMarketGroups[altMarketGroupName] = this.expandFirst && index === 0
          }
          if (!(altMarketGroupName in newExpandedAltMarketSelections)) {
            newExpandedAltMarketSelections[altMarketGroupName] = false // default collapsed
          }
        })

        this.expandedAltMarketGroups = newExpandedAltMarketGroups
        this.expandedAltMarketSelections = newExpandedAltMarketSelections
      }
    }
  },

  mounted () {
    // Open the first alt market group on mount if expandFirst is true
    if (this.expandFirst && !lodash.isEmpty(this.expandedAltMarketGroups)) {
      Vue.set(this.expandedAltMarketGroups, Object.keys(this.expandedAltMarketGroups)[0], true)
    }
  }
}
</script>

<style lang="stylus" scoped>
.show-more-btn
  display: flex
  justify-content: center

.alt-handicaps-header
  display: flex
  justify-content: space-between
  font-size: 12px
  padding: 10px 15px
  margin-bottom: 10px
  border-bottom: 1px solid #e0e0e0

.alt-handicaps-header-name
  display: flex
  width: 50%
  justify-content: center
  font-size: 14px
  font-family: "Roboto Condensed"
</style>



// WEBPACK FOOTER //
// src/components/sports/bettingoffer/AltMarkets.vue